import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { useServiceStore } from '@/stores/serviceStore'
import { useGenerationStore } from '@/stores/generationStore'
import servicesData from '@/data/services.json'

export default function GeneratorPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const { selectedServiceIds, toggleService, clearSelection } = useServiceStore()
  const { isGenerating, generate } = useGenerationStore()

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const filteredCategories = servicesData.categories.map((category) => ({
    ...category,
    services: category.services.filter(
      (service) =>
        service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.services.length > 0)

  const handleGenerate = async () => {
    if (selectedServiceIds.length === 0) {
      alert('サービスを1つ以上選択してください')
      return
    }
    await generate({
      project_name: 'my-project',
      environment: 'dev',
      aws_region: 'ap-northeast-1',
      services: selectedServiceIds.map((id) => ({
        service_id: id,
        parameters: {},
      })),
    })
    navigate('/preview')
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left: Service Selection */}
        <div className="lg:w-1/2">
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">AWSサービス選択</h2>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="サービスを検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Service Categories */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {filteredCategories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded-lg">
                  <button
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <span className="font-medium text-gray-900">{category.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {category.services.filter((s) => selectedServiceIds.includes(s.id)).length}/
                        {category.services.length}
                      </span>
                      {expandedCategories.includes(category.id) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                  {expandedCategories.includes(category.id) && (
                    <div className="px-3 pb-3 space-y-2">
                      {category.services.map((service) => (
                        <label
                          key={service.id}
                          className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedServiceIds.includes(service.id)}
                            onChange={() => toggleService(service.id)}
                            className="checkbox mt-0.5"
                          />
                          <div>
                            <span className="font-medium text-gray-900">{service.name}</span>
                            <p className="text-sm text-gray-500">{service.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Selected Services & Actions */}
        <div className="lg:w-1/2">
          <div className="card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              選択中のサービス ({selectedServiceIds.length})
            </h2>

            {selectedServiceIds.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                サービスを選択してください
              </p>
            ) : (
              <div className="space-y-2 mb-6">
                {selectedServiceIds.map((serviceId) => {
                  const service = servicesData.categories
                    .flatMap((c) => c.services)
                    .find((s) => s.id === serviceId)
                  if (!service) return null
                  return (
                    <div
                      key={serviceId}
                      className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                    >
                      <span className="font-medium text-gray-900">{service.name}</span>
                      <button
                        onClick={() => toggleService(serviceId)}
                        className="text-sm text-red-500 hover:text-red-700"
                      >
                        削除
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={clearSelection}
                className="btn btn-outline flex-1"
                disabled={selectedServiceIds.length === 0}
              >
                クリア
              </button>
              <button
                onClick={handleGenerate}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                disabled={selectedServiceIds.length === 0 || isGenerating}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    生成中...
                  </>
                ) : (
                  'コード生成'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
